import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, styled, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import { CONTROL_CODE, MINUS_CODE, SLASH_CODE } from 'common/constants/general';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import Logo from 'components/Logo';
import { ShortcutHelperProps } from './ShortcutHelper.props';
import Shortcuts from './Shortcuts';

// Styled components for keyboard shortcut items
export const StyledAction = styled('span')(({ theme }) => ({
  color: theme.palette.grey[400],
}));

export const StyledCombination = styled('span')(({ theme }) => ({
  color: theme.palette.grey[400],
  fontWeight: 'bold',
}));

// Styled component for dialog title
const StyledDialogTitle = styled(DialogTitle)(() => ({
  paddingLeft: '2.5rem',
}));

const ShortcutHelper = ({ children }: ShortcutHelperProps) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);

  // use user state additionally to know if we should display the helper or not
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { user } = storeValues;
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'));

  const startPressingControl = useRef<number | null>(null);

  const onCloseHandler = useCallback(() => setShowDialog(false), [setShowDialog]);
  const maxWidth = upMd ? 'lg' : 'xs';

  useEffect(() => {
    const listenForControlDown = (event: KeyboardEvent) => {
      const pressedKey = event.key || event.code;
      if (pressedKey === CONTROL_CODE && !event.repeat && !isEmpty(user)) {
        startPressingControl.current = event.timeStamp;
      }
    };

    const listenForCombination = (event: KeyboardEvent) => {
      /* account for different keyboard language configurations
      valid codes (not keys) to get a question mark:
        - "Slash" in English Keyboard
        - "Minus" in Spanish keyboard
        (despite showing Quote and Question Mark symbols, the key code is Minus)
      */
      const isValidCode = [SLASH_CODE, MINUS_CODE].includes(event.code);
      if (event.ctrlKey && event.shiftKey && isValidCode && !isEmpty(user)) {
        event.preventDefault();
        setShowDialog(true);
      }
    };

    document.addEventListener('keydown', listenForControlDown);
    document.addEventListener('keydown', listenForCombination);
    return () => {
      document.removeEventListener('keydown', listenForControlDown);
      document.removeEventListener('keydown', listenForCombination);
    };
  }, [user]);

  return (
    <>
      {children}
      <Dialog onClose={onCloseHandler} open={showDialog} fullWidth maxWidth={maxWidth}>
        <StyledDialogTitle>
          <Logo variation="onlyWord" lightBlue />
        </StyledDialogTitle>

        <DialogContent style={{ padding: '2.5rem' }}>
          <Shortcuts />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(ShortcutHelper);
